/**
 * 같은 제목의 팝업을 **한 장으로** 묶는다 — 「같은 날짜면 동일 항목에」(2026-10-26 사용자 요구).
 *
 * ## ⚠️ 축이 「소비자 종류」가 아니라 「제목이 같은가」다
 *
 * 업데이트 내역 팝업은 **묶음마다 한 행**이고 묶음은 (surface 집합, audiences, 날짜)로 갈린다.
 * 그래서 한 사람에게 같은 날짜의 팝업이 **여럿** 보일 수 있다 — 예컨대 `{APP,WEB}` 묶음과
 * `{ADMIN,APP,WEB}` 묶음을 둘 다 받는 WEB 사용자가 그렇다. 사용자 요구는 그 둘을 한 장에
 * 넣는 것이고, **그 「같은 날짜」는 제목이 이미 들고 있다**(제목 생성 함수가 날짜만으로 만든다).
 *
 * ⭐ **그래서 서버 변경이 필요 없다** — `title`은 판정 응답에 이미 실려 온다. 종전에 이 기능을
 * 「소스 구분자가 응답에 없어 불가」로 판정한 것은 **축을 「소비자 종류」로 잘못 잡은 것**이었다.
 *
 * ## ⚠️ 이것은 «파싱»이 아니라 «동등 비교»다
 *
 * 요청자 쪽 `bundleSourceKey`의 docblock이 「팝업 쪽에서 이 문자열을 파싱하지 말 것」이라
 * 금지하는 대상은 **`sourceKey`**(불투명 키)이고 제목에는 걸리지 않는다. 그리고 여기서 하는
 * 일은 날짜를 **뽑아 쓰는 것이 아니라 두 제목이 같은지만 보는 것**이라 **형식에 의존하지
 * 않는다** — 제목 형식이 바뀌어도 「같은 것끼리 묶는다」는 여전히 참이다.
 * ⛔ 그러니 이 파일에 날짜 정규식·`slice`·`split`을 넣지 말 것. 넣는 순간 그 금지에 걸리고,
 *    형식이 바뀌는 날 **red 없이** 조용히 틀린다.
 *
 * ## ⚠️ 한계 둘 — 알고 쓰는 것이다
 *
 * **ⓐ 운영자가 같은 제목 공지를 둘 만들면 합쳐진다.** 오늘 그런 데이터는 없고(2026-10-26 prod
 * 실측), 제목이 같으면 한 장에 모이는 것이 오히려 자연스럽다. 갈라야 할 이유가 생기면 그때
 * 축을 바꾸는 것이 맞고, 그 신호는 「제목이 같은데 내용이 무관한 공지」가 실제로 나타나는 것이다.
 *
 * **ⓑ 「같은 날짜 ≡ 같은 제목」은 «제목을 날짜만으로 만들기 때문»이고, 그것이 암묵 계약이다.**
 * 제목 생성 자리가 형식을 바꾸면(예: 제목에 surface를 넣으면) 같은 날짜의 묶음들이 **서로 다른
 * 제목**을 갖게 되어 합치기가 **조용히 쪼개진다** — tsc도 spec도 그 축을 못 본다(둘 다 「같은
 * 제목끼리 묶였나」만 재고, 「같은 날짜가 같은 제목인가」는 그쪽 함수가 소유한다).
 * ⇒ 그 자리는 **`release-note-popup.service.ts`의 `bundleTitle`**이다(심볼로 가리킨다 — 줄
 * 번호로 쓰면 위에 줄이 끼면 밀리고 red가 아니라 조용히 낡는다). 그 함수를 고치는 사람이
 * **이 문단을 함께 읽어야** 하므로, 그쪽 JSDoc도 이 축을 적고 있어야 한다.
 */

export interface PopupTitleGroup<T> {
  /** 묶음 키이자 화면에 뜨는 제목 — 그룹 안 모든 항목이 공유한다 */
  title: string;
  /** 받은 순서 그대로. 서버 정렬이 `publishedAt desc`라 **최신이 먼저**다(pcon 10-3) */
  items: readonly T[];
}

/**
 * 제목이 같은 것끼리 묶는다 — **첫 등장 순서를 유지**한다.
 *
 * ⚠️ 순서 유지가 계약이다. 서버가 `publishedAt desc`로 주므로 그 순서를 보존하면 pcon 10-3의
 * 「최신 묶음 먼저」가 **공짜로** 따라온다 — 여기서 다시 정렬하면 그 보장을 두 자리가 갖게 되고,
 * 서버 정렬이 바뀌는 날 둘이 어긋난다. `Map`의 삽입 순서에 기대지 않고 `order` 배열로 **명시**한다
 * (키가 숫자 모양 문자열이면 `Map`은 아니지만 객체는 순서가 바뀐다 — 그 함정을 피한다).
 */
export function groupByTitle<T extends { title: string }>(items: readonly T[]): PopupTitleGroup<T>[] {
  const order: string[] = [];
  const byTitle = new Map<string, T[]>();

  for (const item of items) {
    const bucket = byTitle.get(item.title);
    if (bucket) {
      bucket.push(item);
    } else {
      byTitle.set(item.title, [item]);
      order.push(item.title);
    }
  }

  return order.map((title) => ({ title, items: byTitle.get(title) as T[] }));
}
